import {
  Component,
  Input,
  OnInit,
  OnDestroy,
  Output,
  EventEmitter,
  ViewChildren,
  QueryList,
  AfterViewInit, ViewChild
} from '@angular/core';
import {UntypedFormGroup, UntypedFormBuilder, UntypedFormControl, Validators} from '@angular/forms';
import {ModalInterface} from '../../modal.interface';
import {Subscription, Observable, Subject, mergeMap, of} from 'rxjs';
import {ModalState} from '../../modal-state.enum';
import {ModalStateEvent} from "../../modal-state-event";
import {FiltersDataProviderService} from "../../../services/filters.data-provider.service";
import {VehicleTypeViewModel} from "../../../../core/models/vehicle-type.view-model";
import {MakeViewModel} from "../../../../core/models/make.view-model";
import {VehicleViewModel} from "../../../../features/user/garage/vehicle.view-model";
import {BodyTypeViewModel} from "../../../../core/models/body-type.view-model";
import {ModelViewModel} from "../../../../core/models/model.view-model";
import {BodyGenViewModel} from "../../../../core/models/body-gen.view-model";
import {EditionViewModel} from "../../../../core/models/edition.view-model";
import {Dropdown} from "primeng/dropdown";
import {VehicleTypeEnum} from "../../../../core/enums/vehicle-type.enum";
import {TabView} from "primeng/tabview";
import {AbstractDictionaryViewModel} from "../../../../core/models/abstract-dictionary.view-model";
import {AbstractDictionaryApiModel} from "../../../../api/models/abstract-dictionary.api-model";
import {TransmissionInterface} from "../../../../features/user/project/parameters/transmission-interface";
import {ConfirmationService} from "primeng/api";
import {TranslateService} from "@ngx-translate/core";
import {VehicleCatalogSelection} from '../../../../core/models/vehicle-catalog-selection';

@Component({
  selector: 'app-add-vehicle',
  templateUrl: './add-vehicle.component.html',
  styleUrls: ['./add-vehicle.component.scss'],
  providers: [FiltersDataProviderService]
})
export class AddVehicleComponent implements OnInit, AfterViewInit, OnDestroy, ModalInterface {
  private readonly motorcycleLikeVehicleTypeCodes: string[] = [VehicleTypeEnum.MOTORCYCLE, 'ATV', 'QUAD'];
  get state(): Observable<ModalStateEvent> {
    return this._state;
  }

  set state(value: Observable<ModalStateEvent>) {
    this._state = value;
  }


  get subject(): Subject<ModalStateEvent> {
    return this._subject;
  }

  set subject(value: Subject<ModalStateEvent>) {
    this._subject = value;
  }

  public data: UntypedFormGroup;
  private _state: Observable<ModalStateEvent>;
  private _subject: Subject<ModalStateEvent> = new Subject<ModalStateEvent>();
  public vehicleForm: UntypedFormGroup;
  public selectedVehicleTypeVM: VehicleTypeViewModel;
  public vehicleTypes: VehicleTypeViewModel[];
  public makes: MakeViewModel[];
  public bodyTypes: BodyTypeViewModel[];
  public models: ModelViewModel[];
  public bodyGens: BodyGenViewModel[];
  public editions: EditionViewModel[] = [];
  public transmissions: TransmissionInterface[] = [];
  public vehicle: VehicleViewModel;
  private subscription: Subscription;
  private readonly restorationSubscriptions: Subscription[] = [];
  private formValueSubscription?: Subscription;
  private initialSelectionRestored = false;
  @Input() public initialSelection: VehicleCatalogSelection | null = null;
  @Output() vehicleSelected: EventEmitter<VehicleViewModel | null> = new EventEmitter<VehicleViewModel | null>();
  @Output() catalogSelectionChanged = new EventEmitter<VehicleCatalogSelection>();
  @ViewChild(TabView, { static: true }) tabView: TabView;
  @ViewChildren(Dropdown)
  private queryList: QueryList<Dropdown>;
  constructor(
    public addVehicleDPS: FiltersDataProviderService,
    private fb: UntypedFormBuilder,
    private readonly confirmationService: ConfirmationService,
    private readonly translate: TranslateService,
  ) {
  }

  public ngOnInit(): void {
    this.createVehicleForm();
    this.formValueSubscription = this.vehicleForm.valueChanges.subscribe(() => this.emitCatalogSelection());
    this.seekVehicleTypes();
  }

  ngAfterViewInit(): void {
    this.queryList.forEach((e: Dropdown) => {
      e.el.nativeElement.removeAttribute('pautofocus');
      e.el.nativeElement.setAttribute('inputmode', 'none');
      e.focusInputViewChild?.nativeElement.removeAttribute('pautofocus');
      e.focusInputViewChild?.nativeElement.setAttribute('inputmode', 'none');
    });
  }

  public ngOnDestroy(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
    this.formValueSubscription?.unsubscribe();
    this.restorationSubscriptions.forEach((subscription) => subscription.unsubscribe());
  }

  public isCarVehicleType(code: string): boolean {
    return code === VehicleTypeEnum.CAR;
  }

  public isMotorcycleLikeVehicleType(code: string): boolean {
    return this.motorcycleLikeVehicleTypeCodes.includes(code);
  }

  /**
   * THIS DECLARATION MUST BE IN THE SAME FLOW ORDER AS IN THE VIEW BECAUSE disableRelatedChoices METHOD WILL BROKE.
   */
  private createVehicleForm(): void {
    this.vehicleForm = this.fb.group({
      make: new UntypedFormControl({value: '', disabled: true}, [Validators.required]),
      bodyType: new UntypedFormControl({value: '', disabled: true}, [Validators.required]),
      model: new UntypedFormControl({value: '', disabled: true}, [Validators.required]),
      bodyGen: new UntypedFormControl({value: '', disabled: true}, [Validators.required]),
      edition: new UntypedFormControl({value: '', disabled: true}, [Validators.required]),
      transmission: new UntypedFormControl({value: '', disabled: true}, [Validators.required]),
    });
  }

  public seekVehicleTypes(): void {
    this.addVehicleDPS.getVehicleTypeCollection()
      .subscribe((vehicleTypeVMs: VehicleTypeViewModel[]) =>  {
        this.vehicleTypes = vehicleTypeVMs;
        if (this.initialSelection?.vehicleTypeId) {
          this.restoreInitialSelection();
          return;
        }
        this.selectVehicleType(0);
      });
  }

  public selectVehicleType(index: number): void {
    const vehicleType = this.vehicleTypes?.[index];
    // PrimeNG can emit the active tab more than once while its panels are
    // being mounted. Re-fetching the same type cancels the in-flight request
    // for makes, leaving the catalog in an endless loading state.
    if (!vehicleType || this.selectedVehicleTypeVM?.id === vehicleType.id) {
      return;
    }
    this.seekMakes(vehicleType, ['make', 'model', 'bodyType', 'bodyGen', 'edition', 'transmission']);
  }

  public seekMakes(vehicleTypeVM: VehicleTypeViewModel, relatedChoices: string[]): void {
    this.selectedVehicleTypeVM = vehicleTypeVM;
    this.emitCatalogSelection();
    this._subject.next(new ModalStateEvent(ModalState.PROCESS));
    this.disableRelatedChoices(relatedChoices);
    this.unsubscribePending();
    this.subscription = this.addVehicleDPS.getMakeCollection([vehicleTypeVM]).subscribe(
      (makeVMs: MakeViewModel[]) => {
        this.makes = makeVMs;
        this.vehicleForm.get('make').enable();
        this._subject.next(new ModalStateEvent(ModalState.PROCESS_END));
      },
      () => {
        this._subject.next(new ModalStateEvent(ModalState.PROCESS_END));
      }
    );
  }

  public seekBodyTypes(
    vms: AbstractDictionaryViewModel<AbstractDictionaryApiModel>[][],
    relatedChoices: string[] = []
  ): void {
    this._subject.next(new ModalStateEvent(ModalState.PROCESS));
    this.disableRelatedChoices(relatedChoices);
    this.unsubscribePending();
    this.subscription = this.addVehicleDPS.getBodyTypeCollection(vms).subscribe(
      (bodyTypeVMs: BodyTypeViewModel[]) => {
        this.bodyTypes = bodyTypeVMs;
        this.vehicleForm.get('bodyType').enable();
        this._subject.next(new ModalStateEvent(ModalState.PROCESS_END));
      },
      error => {

      }
    );
  }

  public seekModels(
    vms: AbstractDictionaryViewModel<AbstractDictionaryApiModel>[][],
    relatedChoices: string[] = []
  ): void {
    this._subject.next(new ModalStateEvent(ModalState.PROCESS));
    this.disableRelatedChoices(relatedChoices);
    this.unsubscribePending();
    this.subscription = this.addVehicleDPS.getModelCollection(vms).subscribe(
      (modelVMs: ModelViewModel[]) => {
        this.models = modelVMs;
        this.vehicleForm.get('model').enable();
        this._subject.next(new ModalStateEvent(ModalState.PROCESS_END));
      },
      error => {

      }
    );
  }

  public seekBodyGens(
    vms: AbstractDictionaryViewModel<AbstractDictionaryApiModel>[][],
    relatedChoices: string[] = []
  ): void {
    this._subject.next(new ModalStateEvent(ModalState.PROCESS));
    this.disableRelatedChoices(relatedChoices);
    this.unsubscribePending();
    this.subscription = this.addVehicleDPS.getBodyGenCollection(vms).subscribe(
      (bodyGenVMs: BodyGenViewModel[]) => {
        this.bodyGens = this.prepareBodyGenLabels(bodyGenVMs);
        this.vehicleForm.get('bodyGen').enable();
        this._subject.next(new ModalStateEvent(ModalState.PROCESS_END));
      },
      error => {

      }
    );
  }

  public seekEditions(
    vms: AbstractDictionaryViewModel<AbstractDictionaryApiModel>[][],
    relatedChoices: string[] = []
  ): void {
    this._subject.next(new ModalStateEvent(ModalState.PROCESS));
    this.disableRelatedChoices(relatedChoices);
    this.unsubscribePending();
    this.subscription = this.addVehicleDPS.getEditionCollection(vms).pipe(mergeMap((editionVMs: EditionViewModel[]) => {
      if (editionVMs.length) {
        this.editions = this.prepareEditionLabels(editionVMs);
        this.vehicleForm.get('edition').enable();
        this._subject.next(new ModalStateEvent(ModalState.PROCESS_END));
      } else {
        this.seekTransmissions();
      }
      return of(editionVMs);
    })).subscribe();
  }

  public seekTransmissions(relatedChoices: string[] = []): void {
    this._subject.next(new ModalStateEvent(ModalState.PROCESS));
    this.disableRelatedChoices(relatedChoices);
    this.unsubscribePending();
    this.subscription = this.addVehicleDPS.getTransmissionCollectionByVehicleParams(this.vehicleForm.value)
      .subscribe((transmissions: TransmissionInterface[]) => {
        this.transmissions = transmissions;
        if (!this.transmissions.length) {
          this.seekVehicle();
          return;
        }

        this.vehicleForm.get('transmission').enable();
        if (this.transmissions.length === 1) {
          this.vehicleForm.get('transmission').setValue(this.transmissions[0]);
          this.seekVehicle();
          return;
        }

        this._subject.next(new ModalStateEvent(ModalState.PROCESS_END));
      });
  }

  public seekVehicle(): void {
    this._subject.next(new ModalStateEvent(ModalState.PROCESS));
    this.unsubscribePending();
    this.addVehicleDPS.getVehicleByParams(this.vehicleForm.value)
      .subscribe((vehicles: VehicleViewModel[]) => {
        const vehicleVM = vehicles[0] || null;
        if (!vehicleVM) {
          this.markSelectionAsInvalid();
          this._subject.next(new ModalStateEvent(ModalState.PROCESS_END));
          return;
        }
        vehicleVM.vehicleType = this.selectedVehicleTypeVM;
        this.vehicleSelected.emit(vehicleVM);
        this._subject.next(new ModalStateEvent(ModalState.VALID, vehicleVM));
      });
  }

  public clearAfterMakeSelection(): void {
    this.unsubscribePending();
    this.disableRelatedChoices(['model', 'bodyType', 'bodyGen', 'edition', 'transmission']);
    this.markSelectionAsInvalid();
  }

  public clearAfterModelSelection(): void {
    this.unsubscribePending();
    this.disableRelatedChoices(['bodyType', 'bodyGen', 'edition', 'transmission']);
    this.markSelectionAsInvalid();
  }

  public clearAfterBodyTypeSelection(): void {
    this.unsubscribePending();
    this.disableRelatedChoices(['bodyGen', 'edition', 'transmission']);
    this.markSelectionAsInvalid();
  }

  public clearAfterBodyGenSelection(): void {
    this.unsubscribePending();
    this.disableRelatedChoices(['edition', 'transmission']);
    this.markSelectionAsInvalid();
  }

  public clearAfterEditionSelection(): void {
    this.unsubscribePending();
    this.disableRelatedChoices(['transmission']);
    this.markSelectionAsInvalid();
  }

  public clearAfterTransmissionSelection(): void {
    this.unsubscribePending();
    this.markSelectionAsInvalid();
  }

  /**
   * TODO: Refactor this, as this method require specific declaration flow of form fields;
   * @param {string} relatedChoices
   */
  private disableRelatedChoices(relatedChoices: string[]): void {
    for (const key in this.vehicleForm.controls) {
      if (relatedChoices.includes(key)) {
        this.vehicleForm.controls[key].reset();
        this.vehicleForm.controls[key].disable({onlySelf: true, emitEvent: false});
      }
    }
    if (relatedChoices.includes('make')) {
      this.makes = [];
    }
    if (relatedChoices.includes('model')) {
      this.models = [];
    }
    if (relatedChoices.includes('bodyType')) {
      this.bodyTypes = [];
    }
    if (relatedChoices.includes('bodyGen')) {
      this.bodyGens = [];
    }
    if (relatedChoices.includes('edition')) {
      this.editions = [];
    }
    if (relatedChoices.includes('transmission')) {
      this.transmissions = [];
    }
  }

  private unsubscribePending(): void {
    if (this.subscription instanceof Subscription) {
      this.subscription.unsubscribe();
    }
  }

  private prepareBodyGenLabels(bodyGenVMs: BodyGenViewModel[]): BodyGenViewModel[] {
    const labelCountMap = new Map<string, number>();
    bodyGenVMs.forEach((bodyGenVM: BodyGenViewModel) => {
      const label = bodyGenVM.displayName || bodyGenVM.name;
      labelCountMap.set(label, (labelCountMap.get(label) || 0) + 1);
    });

    bodyGenVMs.forEach((bodyGenVM: BodyGenViewModel) => {
      const baseLabel = bodyGenVM.displayName || bodyGenVM.name;
      bodyGenVM.displayName = (labelCountMap.get(baseLabel) || 0) > 1
        ? `${baseLabel} [#${bodyGenVM.id}]`
        : baseLabel;
    });

    return bodyGenVMs;
  }

  private prepareEditionLabels(editionVMs: EditionViewModel[]): EditionViewModel[] {
    return editionVMs.map((editionVM: EditionViewModel) => {
      editionVM.displayName = editionVM.displayName || editionVM.fullName || editionVM.name;
      return editionVM;
    });
  }

  private markSelectionAsInvalid(): void {
    this.vehicle = null;
    this.vehicleSelected.emit(null);
    this.emitCatalogSelection();
    this._subject.next(new ModalStateEvent(ModalState.INVALID));
  }

  private emitCatalogSelection(): void {
    if (!this.vehicleForm) {
      return;
    }
    const value = this.vehicleForm.getRawValue();
    this.catalogSelectionChanged.emit({
      vehicleTypeId: this.selectedVehicleTypeVM?.id,
      makeId: value.make?.id,
      modelId: value.model?.id,
      bodyTypeId: value.bodyType?.id,
      bodyGenId: value.bodyGen?.id,
      editionId: value.edition?.id,
      transmissionId: this.itemId(value.transmission),
    });
  }

  private restoreInitialSelection(): void {
    if (this.initialSelectionRestored || !this.initialSelection?.vehicleTypeId || !this.vehicleTypes?.length) {
      return;
    }
    this.initialSelectionRestored = true;
    const vehicleType = this.findById(this.vehicleTypes, this.initialSelection.vehicleTypeId);
    if (!vehicleType) {
      return;
    }
    this.selectedVehicleTypeVM = vehicleType;
    const selectedIndex = this.vehicleTypes.findIndex((item) => item.id === vehicleType.id);
    this.tabView.activeIndex = selectedIndex >= 0 ? selectedIndex : 0;
    this.restoreMakes(vehicleType);
  }

  private restoreMakes(vehicleType: VehicleTypeViewModel): void {
    this.trackRestoration(this.addVehicleDPS.getMakeCollection([vehicleType]).subscribe((makes: MakeViewModel[]) => {
      this.makes = makes;
      this.vehicleForm.get('make').enable({emitEvent: false});
      const make = this.findById(makes, this.initialSelection?.makeId);
      if (!make) {
        return;
      }
      this.setRestoredValue('make', make);
      this.restoreModels(vehicleType, make);
    }));
  }

  private restoreModels(vehicleType: VehicleTypeViewModel, make: MakeViewModel): void {
    this.trackRestoration(this.addVehicleDPS.getModelCollection([[vehicleType], [make]]).subscribe((models: ModelViewModel[]) => {
      this.models = models;
      this.vehicleForm.get('model').enable({emitEvent: false});
      const model = this.findById(models, this.initialSelection?.modelId);
      if (!model) {
        return;
      }
      this.setRestoredValue('model', model);
      this.restoreBodyTypes(vehicleType, make, model);
    }));
  }

  private restoreBodyTypes(vehicleType: VehicleTypeViewModel, make: MakeViewModel, model: ModelViewModel): void {
    this.trackRestoration(this.addVehicleDPS.getBodyTypeCollection([[vehicleType], [make], [model]]).subscribe((bodyTypes: BodyTypeViewModel[]) => {
      this.bodyTypes = bodyTypes;
      this.vehicleForm.get('bodyType').enable({emitEvent: false});
      const bodyType = this.findById(bodyTypes, this.initialSelection?.bodyTypeId);
      if (!bodyType) {
        return;
      }
      this.setRestoredValue('bodyType', bodyType);
      if (this.isCarVehicleType(vehicleType.code)) {
        this.restoreBodyGens(vehicleType, make, model, bodyType);
        return;
      }
      this.restoreEditions(vehicleType, make, model, bodyType, null);
    }));
  }

  private restoreBodyGens(vehicleType: VehicleTypeViewModel, make: MakeViewModel, model: ModelViewModel, bodyType: BodyTypeViewModel): void {
    this.trackRestoration(this.addVehicleDPS.getBodyGenCollection([[vehicleType], [make], [model], [bodyType]]).subscribe((bodyGens: BodyGenViewModel[]) => {
      this.bodyGens = this.prepareBodyGenLabels(bodyGens);
      this.vehicleForm.get('bodyGen').enable({emitEvent: false});
      const bodyGen = this.findById(this.bodyGens, this.initialSelection?.bodyGenId);
      if (!bodyGen) {
        return;
      }
      this.setRestoredValue('bodyGen', bodyGen);
      this.restoreEditions(vehicleType, make, model, bodyType, bodyGen);
    }));
  }

  private restoreEditions(
    vehicleType: VehicleTypeViewModel,
    make: MakeViewModel,
    model: ModelViewModel,
    bodyType: BodyTypeViewModel,
    bodyGen: BodyGenViewModel | null,
  ): void {
    const params: AbstractDictionaryViewModel<AbstractDictionaryApiModel>[][] = [[vehicleType], [make], [model], [bodyType]];
    if (bodyGen) {
      params.push([bodyGen]);
    }
    this.trackRestoration(this.addVehicleDPS.getEditionCollection(params).subscribe((editions: EditionViewModel[]) => {
      this.editions = this.prepareEditionLabels(editions);
      this.vehicleForm.get('edition').enable({emitEvent: false});
      const edition = this.findById(this.editions, this.initialSelection?.editionId);
      if (edition) {
        this.setRestoredValue('edition', edition);
      }
      this.restoreTransmissions();
    }));
  }

  private restoreTransmissions(): void {
    this.trackRestoration(this.addVehicleDPS.getTransmissionCollectionByVehicleParams(this.vehicleForm.getRawValue()).subscribe((transmissions: TransmissionInterface[]) => {
      this.transmissions = transmissions;
      this.vehicleForm.get('transmission').enable({emitEvent: false});
      const transmission = this.findById(transmissions, this.initialSelection?.transmissionId);
      if (transmission) {
        this.setRestoredValue('transmission', transmission);
      }
      if (!transmission && transmissions.length === 1) {
        this.setRestoredValue('transmission', transmissions[0]);
      }
      if (this.vehicleForm.get('transmission').value || !transmissions.length) {
        this.restoreVehicle();
      }
    }));
  }

  private restoreVehicle(): void {
    this.trackRestoration(this.addVehicleDPS.getVehicleByParams(this.vehicleForm.getRawValue()).subscribe((vehicles: VehicleViewModel[]) => {
      const vehicle = vehicles[0] || null;
      if (!vehicle) {
        return;
      }
      vehicle.vehicleType = this.selectedVehicleTypeVM;
      this.vehicle = vehicle;
      this.vehicleSelected.emit(vehicle);
      this.emitCatalogSelection();
    }));
  }

  private setRestoredValue(controlName: string, value: unknown): void {
    this.vehicleForm.get(controlName).enable({emitEvent: false});
    this.vehicleForm.get(controlName).setValue(value, {emitEvent: false});
  }

  private findById<T>(items: T[] | undefined, id: number | undefined): T | null {
    return id === undefined ? null : items?.find((item) => this.itemId(item) === id) || null;
  }

  private itemId(value: {id?: number; '@id'?: string} | null | undefined): number | undefined {
    if (Number.isFinite(value?.id)) {
      return value.id;
    }
    const id = Number(value?.['@id']?.split('/').pop());
    return Number.isFinite(id) ? id : undefined;
  }

  private trackRestoration(subscription: Subscription): void {
    this.restorationSubscriptions.push(subscription);
  }

  public beforeModalCloseAttempt(): Promise<boolean> {
    if (!this.hasUnsavedVehicleSelection()) {
      return Promise.resolve(true);
    }

    return new Promise((resolve) => {
      this.confirmationService.confirm({
        header: this.translate.instant('shared.modal.modal_service.add_project.exit_process_title'),
        message: this.translate.instant('shared.modal.modal_service.add_project.exit_process_message'),
        accept: () => resolve(true),
        reject: () => resolve(false),
      });
    });
  }

  private hasUnsavedVehicleSelection(): boolean {
    if (!this.vehicleForm) {
      return false;
    }

    if (this.vehicleForm.dirty) {
      return true;
    }

    return Object.values(this.vehicleForm.controls).some((control: UntypedFormControl) => {
      const value = control?.value;
      return value !== null && value !== undefined && value !== '';
    });
  }

  protected readonly VehicleTypeEnum = VehicleTypeEnum;
}
